import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import {
  getAllClusterEnvs,
  getAllClusterSize,
  getAllInspectionRules,
  getClusterAdminList,
  getClusterDetailByName,
  getClusterList,
  getInspectionRuleExecuteResults,
  getRunningLog,
  getUnhealthResultDetail,
} from '@/services/kopClusterService';
import { isVoid } from '@/utils/common';
import { CLUSTER_TYPES } from '@/utils/constant';

// 执行步骤
export const QUICK_DEPLOY_STEPS = Object.freeze({
  SELECT_CLUSTER_TYPE: 'SELECT_CLUSTER_TYPE',
  CHOICE_CLUSTER: 'CHOICE_CLUSTER',
  MAINTAIN_HOSTS: 'MAINTAIN_HOSTS',
  MAINTAIN_K8S: 'MAINTAIN_K8S',
});

const initLogDetail = {
  readNum: 0,
  startLine: 0,
  total: 0,
  text: '',
  fromNum: 0,
  loading: false,
};

const initialState = {
  loading: false,
  detailLoading: false,
  basic: {},
  detail: {},
  list: [],
  chartLoading: false,
  chartsData: {}, // 图表数据
  excuteResultList: [],
  excuteResultListFetching: false,
  rules: [],
  listBackup: [],
  allList: [],
  clusterEnvs: [],
  filterOptions: [
    { value: 'time', label: '接入时间' },
    { value: 'messageSize', label: '消息总大小' },
    { value: 'topicCount', label: 'Topic总数' },
  ],
  orderOptions: [
    { value: 'desc', label: '降序' },
    { value: 'asc', label: '升序' },
  ],
  unhealthResult: {},
  fetchingUnHealth: false,
  quickDeploy: {
    currentStep: QUICK_DEPLOY_STEPS.SELECT_CLUSTER_TYPE,
    clusterType: CLUSTER_TYPES.HOST, // 主机
    clusterInfo: {},
  },
  logDetail: initLogDetail,
  historyOpen: false,
  adminOptions: [],
};

export const fetchClusterAdminList = createAsyncThunk(
  'kopCluster/fetchClusterAdminList',
  async () => {
    const response = await getClusterAdminList();
    return response.data;
  },
);

export const fetchBasicAsync = createAsyncThunk('kopCluster/fetchClusterSize', async () => {
  const response = await getAllClusterSize();
  return response.data;
});

// 获取健康度状态结果
export const fetchInspectionRuleExecuteResults = createAsyncThunk(
  'kopCluster/fetchInspectionRuleExecuteResults',
  async (params) => {
    const response = await getInspectionRuleExecuteResults(params);
    return response.data;
  },
);

// 获取所有健康度配置
export const fetchAllInspectionRules = createAsyncThunk(
  'kopCluster/fetchAllInspectionRules',
  async (clusterType) => {
    const response = await getAllInspectionRules(clusterType);
    return response.data;
  },
);

// 集群列表
export const fetchClusterList = createAsyncThunk(
  'kopCluster/fetchClusterList',
  async (params = {}) => {
    const { sortKey, sortType, ...others } = params;
    const response = await getClusterList(others);
    return response.data || [];
  },
);

export const fetchAllClusterEnvs = createAsyncThunk('kopCluster/fetchAllClusterEnvs', async () => {
  const { data = [] } = await getAllClusterEnvs();

  return data.map((item) => ({
    ...item,
    label: item.envName,
    value: item.envKey,
  }));
});

// 获取集群详情
export const fetchClusterDetailByName = createAsyncThunk(
  'kopCluster/fetchClusterDetailByName',
  async (name) => {
    const response = await getClusterDetailByName(name);
    return response.data;
  },
);

// 异常健康度查询
export const fetchUnhealthResultDetail = createAsyncThunk(
  'kopCluster/fetchUnhealthResultDetail',
  async (name) => {
    const response = await getUnhealthResultDetail(name);
    return response.data;
  },
);

export const fetchRunningLog = createAsyncThunk('kopCluster/fetchRunningLog', async (params) => {
  const response = await getRunningLog(params);
  return response.data;
});

export const fetchInitialRunningLog = createAsyncThunk(
  'kopCluster/fetchInitialRunningLog',
  async (params) => {
    const response = await getRunningLog(params);
    return response.data;
  },
);

// 集群列表 - 所有， 并选中默认
export const fetchAllClusterList = createAsyncThunk(
  'kopCluster/fetchAllClusterList',
  async (defaultName) => {
    const response = await getClusterList();
    let hasSelect = false;
    const result = (response.data || []).map((item) => {
      item.selected = false;
      if (item.clusterName === defaultName) {
        item.selected = true;
        hasSelect = true;
      }
      return item;
    });
    // 再次尝试使用第一个
    if (!hasSelect && result && result.length) {
      result[0].selected = true;
    }
    return result;
  },
);

export const kopClusterSlice = createSlice({
  name: 'kopCluster',
  initialState,
  reducers: {
    doSort: (state, action) => {
      const { sortKey, sortType } = action.payload;
      if (isVoid(sortKey) || isVoid(sortType)) {
        state.list = state.listBackup; // 恢复
        return;
      }
      state.list = state.list.sort((prev, next) => {
        let prevVal = prev[sortKey] || 0;
        let nextVal = next[sortKey] || 0;
        if (sortKey === 'time') {
          prevVal = dayjs(prev.createTime).unix();
          nextVal = dayjs(next.createTime).unix();
        }
        if (sortType === 'desc') {
          return nextVal - prevVal;
        }
        return prevVal - nextVal;
      });
    },
    toggleHistoryOpen: (state) => {
      state.historyOpen = !state.historyOpen;
    },
    selectCuster: (state, action) => {
      const name = action.payload;
      if (name) {
        state.allList = state.allList.map((item) => {
          item.selected = false;
          if (item.clusterName === name) {
            item.selected = true;
          }
          return item;
        });
      }
    },
    setQuickDeployClusterType: (state, action) => {
      const type = action.payload;
      state.quickDeploy.clusterType = type;
    },
    resetQuickDeploy: (state) => {
      state.quickDeploy = initialState.quickDeploy;
    },
    setQuickDeployStep: (state, action) => {
      const step = action.payload;
      state.quickDeploy.currentStep = step;
    },
    setQuickDeployClusterInfo: (state, action) => {
      state.quickDeploy.clusterInfo = action.payload;
    },
    resetClusterDetail: (state) => {
      state.detail = {};
    },
    resetLogState: (state) => {
      state.logDetail = initLogDetail;
    },
    resetFromNum: (state, action) => {
      state.logDetail.fromNum = action.payload;
      state.logDetail.text = '';
    },
    resetAllList: (state) => {
      state.allList = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBasicAsync.fulfilled, (state, action) => {
        state.basic = action.payload;
      })
      .addCase(fetchClusterList.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchClusterList.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload;
        state.listBackup = action.payload;
      })
      .addCase(fetchClusterList.rejected, (state) => {
        state.loading = false;
        state.list = [];
      })
      .addCase(fetchAllClusterList.fulfilled, (state, action) => {
        state.allList = action.payload;
      })
      .addCase(fetchClusterDetailByName.pending, (state) => {
        state.detailLoading = true;
        state.detail = {};
      })
      .addCase(fetchClusterDetailByName.fulfilled, (state, action) => {
        state.detail = action.payload;
        state.detailLoading = false;
      })
      .addCase(fetchClusterDetailByName.rejected, (state) => {
        state.detailLoading = false;
      })
      .addCase(fetchInspectionRuleExecuteResults.pending, (state) => {
        state.excuteResultListFetching = true;
        state.excuteResultList = [];
      })
      .addCase(fetchInspectionRuleExecuteResults.fulfilled, (state, action) => {
        state.excuteResultList = action.payload;
        state.excuteResultListFetching = false;
      })
      .addCase(fetchInspectionRuleExecuteResults.rejected, (state) => {
        state.excuteResultListFetching = false;
      })
      .addCase(fetchAllInspectionRules.fulfilled, (state, action) => {
        state.rules = action.payload;
      })
      .addCase(fetchUnhealthResultDetail.pending, (state) => {
        state.unhealthResult = {};
        state.fetchingUnHealth = true;
      })
      .addCase(fetchUnhealthResultDetail.fulfilled, (state, action) => {
        state.unhealthResult = action.payload;
        state.fetchingUnHealth = false;
      })
      .addCase(fetchUnhealthResultDetail.rejected, (state) => {
        state.fetchingUnHealth = false;
      })
      .addCase(fetchAllClusterEnvs.fulfilled, (state, action) => {
        state.clusterEnvs = action.payload;
      })
      // 均从 index: 1 开始计数
      .addCase(fetchInitialRunningLog.fulfilled, (state, action) => {
        const { totalNum } = action.payload;
        const startLine = totalNum > 1000 ? totalNum - 999 : 1;

        // 下次轮训开始使用
        state.logDetail.startLine = startLine;
        state.logDetail.readNum = totalNum - startLine + 1;
        state.logDetail.total = totalNum;
        state.logDetail.fromNum = startLine; // 缓存初始读取行数
      })
      .addCase(fetchRunningLog.pending, (state) => {
        state.logDetail.loading = true;
      })
      .addCase(fetchRunningLog.rejected, (state) => {
        state.logDetail.loading = false;
      })
      .addCase(fetchRunningLog.fulfilled, (state, action) => {
        const { totalNum, logContent = '', toLineNum } = action.payload;
        const { text } = state.logDetail;
        state.logDetail.total = totalNum;
        state.logDetail.loading = false;
        if (logContent) {
          const txt = text + logContent;
          state.logDetail.text = txt;
        }
        // 如果有新数据，下次继续读。
        // 否则仅轮询
        if (totalNum - toLineNum > 0) {
          state.logDetail.startLine = toLineNum + 1;
          state.logDetail.readNum = totalNum - toLineNum;
        } else {
          state.logDetail.startLine = toLineNum;
          state.logDetail.readNum = 0;
        }
      })
      .addCase(fetchClusterAdminList.fulfilled, (state, action) => {
        state.adminOptions = action.payload.map((item) => ({
          label: item,
          value: item,
        }));
      });
  },
});

export const {
  doSort,
  selectCuster,
  setQuickDeployClusterType,
  resetQuickDeploy,
  setQuickDeployStep,
  setQuickDeployClusterInfo,
  resetClusterDetail,
  resetLogState,
  resetFromNum,
  resetAllList,
  toggleHistoryOpen,
} = kopClusterSlice.actions;
export default kopClusterSlice.reducer;
